import { useCallback } from "react";
import { apiFetch, ApiError, type ApiFetchInit } from "../lib/apiFetch";
import { usePortalSession } from "./usePortalSession";

/** Per-call extras forwarded to `apiFetch`'s options (auth is injected by the hook). */
export interface PortalApiCallOptions {
  /** `"blob"` for authed binary downloads; default `"json"`. */
  responseType?: "json" | "blob";
}

export type PortalApiFetch = <T = unknown>(
  path: string,
  init?: ApiFetchInit,
  opts?: PortalApiCallOptions,
) => Promise<T>;

/**
 * Returns a typed `fetch`-like helper bound to the current portal session.
 *
 * The token is the self-issued portal-session JWT from the session context,
 * stored in localStorage; the portals have no OIDC. The helper keeps its
 * identity until the session changes, so it's safe in `useEffect` deps.
 *
 * Session-expiry safety net: when a call returns a 401 (the server rejected an
 * expired or invalid token), the hook calls `clear()` before rethrowing the
 * `ApiError`. The cleared session makes the portal's authenticated layout
 * redirect to `/login` on the next render; callers still see the throw and can
 * show their own error UI.
 *
 * Usage:
 *   const api = usePortalApi();
 *   const me = await api<PartnerMeResponse>("/api/partner/me");
 */
export function usePortalApi(): PortalApiFetch {
  const { session, clear } = usePortalSession();
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

  return useCallback<PortalApiFetch>(
    async <T = unknown>(
      path: string,
      init: ApiFetchInit = {},
      opts: PortalApiCallOptions = {},
    ): Promise<T> => {
      try {
        // `exactOptionalPropertyTypes`: only set `token` / `responseType` when
        // present so we don't pass `undefined` values to apiFetch.
        const options = {
          baseUrl,
          ...(session ? { token: session } : {}),
          ...(opts.responseType ? { responseType: opts.responseType } : {}),
        };
        return await apiFetch<T>(path, init, options);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // The session is dead, so drop it everywhere; callers still see the throw.
          clear();
        }
        throw err;
      }
    },
    [session, clear, baseUrl],
  );
}
